import { useCallback } from 'react';
import { STATE_BOUNDING_BOXES } from '../components/inputs/stateBoundingBoxes';
import { useGeocoding } from './useGeocoding';
import logger from '@app/libs/logger';

export interface LocationRestriction {
  north: number;
  south: number;
  east: number;
  west: number;
}

export const useLocationRestrictions = (): {
  getLocationRestrictions: (
    city?: string,
    state?: string,
    componentRestrictions?: { country: string },
  ) => Promise<LocationRestriction | undefined>;
} => {
  const { getGeocodeData } = useGeocoding();

  const getLocationRestrictions = useCallback(
    async (
      city?: string,
      state?: string,
      componentRestrictions?: { country: string },
    ): Promise<LocationRestriction | undefined> => {
      let locationRestriction: LocationRestriction | undefined = undefined;

      if (city) {
        // Priority: City bounds first, then state bounds, then no restriction
        try {
          const geocodeResults = await getGeocodeData(city, componentRestrictions);
          if (geocodeResults && geocodeResults[0]?.geometry?.viewport) {
            const viewport = geocodeResults[0].geometry.viewport;
            locationRestriction = {
              north: viewport.northeast.lat,
              south: viewport.southwest.lat,
              east: viewport.northeast.lng,
              west: viewport.southwest.lng,
            };
          }
        } catch (error) {
          logger.exception(error, { city, componentRestrictions });
        }
      } else if (state && STATE_BOUNDING_BOXES[state]) {
        locationRestriction = STATE_BOUNDING_BOXES[state];
      }

      return locationRestriction;
    },
    [getGeocodeData],
  );

  return { getLocationRestrictions };
};
